import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';
import AppShell from '../components/AppShell';
import BodyDamageDiagram from '../components/BodyDamageDiagram';
import { createInspectionReportShareLink, downloadInspectionReportPdf } from '../utils/reportPdf';
import { logoUrl, storageUrl } from '../utils/urls';
import '../styles/report-premium.css';
import {
  DEFAULT_BODY_RESULT_OPTIONS,
  DEFAULT_GENERIC_RESULT_OPTIONS,
  buildResultOptionMap,
  getNumberedResultOptions,
  getResultLookupType,
  normalizeResultOptions,
  readStatusList,
} from '../utils/resultOptions';

// عدد صور المركبة في كل صفحة من التقرير: شبكة ٣×٣ تملأ الصفحة دون أن تتجاوزها.
const GALLERY_IMAGES_PER_PAGE = 9;

const REPORT_THEMES = [
  { key: 'executive', label: 'Identity Red' },
  { key: 'royal', label: 'Navy Night' },
  { key: 'slate', label: 'Black Steel' },
];

const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || 'ScanCar';
const COMPANY_INFO_LINE = process.env.REACT_APP_COMPANY_INFO || 'مركز فحص المركبات • الرياض • 920000000';
const COMPANY_LOGO = process.env.REACT_APP_COMPANY_LOGO || '/lazer.png';
const ARAB_COUNTRY_CODES = [
  { code: '966', label: 'السعودية', dial: '+966' },
  { code: '971', label: 'الإمارات', dial: '+971' },
  { code: '965', label: 'الكويت', dial: '+965' },
  { code: '974', label: 'قطر', dial: '+974' },
  { code: '973', label: 'البحرين', dial: '+973' },
  { code: '968', label: 'عمان', dial: '+968' },
  { code: '967', label: 'اليمن', dial: '+967' },
  { code: '962', label: 'الأردن', dial: '+962' },
  { code: '961', label: 'لبنان', dial: '+961' },
  { code: '963', label: 'سوريا', dial: '+963' },
  { code: '970', label: 'فلسطين', dial: '+970' },
  { code: '964', label: 'العراق', dial: '+964' },
  { code: '20', label: 'مصر', dial: '+20' },
  { code: '249', label: 'السودان', dial: '+249' },
  { code: '218', label: 'ليبيا', dial: '+218' },
  { code: '216', label: 'تونس', dial: '+216' },
  { code: '213', label: 'الجزائر', dial: '+213' },
  { code: '212', label: 'المغرب', dial: '+212' },
  { code: '222', label: 'موريتانيا', dial: '+222' },
  { code: '253', label: 'جيبوتي', dial: '+253' },
  { code: '252', label: 'الصومال', dial: '+252' },
  { code: '269', label: 'جزر القمر', dial: '+269' },
];

function formatReportDate(value) {
  // "2026-08-26" يُفسَّر بتوقيت UTC فينزاح يومًا في بعض المناطق، لذلك نثبّته على التوقيت المحلي.
  const isDateOnly = typeof value === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(value.trim());
  const date = value ? new Date(isDateOnly ? `${value.trim()}T00:00:00` : value) : new Date();
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('ar-SA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function SectionHeading({ ar, en, icon }) {
  return (
    <div className="report-section-title">
      <span className="en">{en}</span>
      <span className="report-section-title-icon" aria-hidden="true">{icon || ''}</span>
      <span className="ar">{ar}</span>
    </div>
  );
}

function InfoIcon({ children, type }) {
  const icons = {
    clipboard: '▤',
    document: '▣',
    refresh: '↻',
    vehicle: '🚘',
    barcode: '▥',
    serial: '#',
    odometer: '◴',
    structure: '⌘',
    topCar: '◇',
    seat: '▣',
    seats: '▥',
    paint: '◒',
    flame: '♨',
    cylinder: '◫',
    engine: '⚙',
    fuel: '⛽',
    gear: '⚙',
  };

  if (icons[type]) {
    return (
      <span className="report-info-icon report-info-icon--text" aria-hidden="true">{icons[type]}</span>
    );
  }

  return <span className="report-info-icon" aria-hidden="true">{children}</span>;
}

function InspectionScoreGauge({ value, min = 0, max = 100, gradientId = 'reportScoreGradient' }) {
  // الحد الأدنى صفر، و«||» كان يحوّله إلى 300 فيصير المدى سالبًا (100 − 300)،
  // فتُحصر القيمة عند 300 وتبقى النسبة صفرًا والإبرة ثابتة مهما كان التقييم.
  const parsedMin = Number(min);
  const parsedMax = Number(max);
  const safeMin = Number.isFinite(parsedMin) ? parsedMin : 0;
  const safeMax = Number.isFinite(parsedMax) && parsedMax > safeMin ? parsedMax : safeMin + 100;
  const rawValue = Number(value);
  const boundedValue = Number.isFinite(rawValue) ? Math.max(safeMin, Math.min(safeMax, rawValue)) : safeMin;
  const span = safeMax - safeMin;
  const percentage = span > 0 ? (boundedValue - safeMin) / span : 0;
  const needleAngle = Math.PI - (Math.PI * percentage);
  const needleX = 100 + (74 * Math.cos(needleAngle));
  const needleY = 100 - (74 * Math.sin(needleAngle));
  const fillLength = Math.max(0, Math.min(100, percentage * 100));

  return (
    <svg viewBox="0 0 200 132" className="report-score-gauge-svg" role="img" aria-label="مؤشر تقييم الفحص">
      <defs>
        <linearGradient id={gradientId} x1="20" y1="100" x2="180" y2="100" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ef4444" />
          <stop offset="45%" stopColor="#f59e0b" />
          <stop offset="70%" stopColor="#eab308" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
      </defs>
      <path
        d="M 24 100 A 76 76 0 0 1 176 100"
        fill="none"
        stroke="#dbe4f0"
        strokeWidth="16"
        strokeLinecap="round"
      />
      <path
        d="M 24 100 A 76 76 0 0 1 176 100"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="16"
        strokeLinecap="round"
        pathLength="100"
        strokeDasharray={`${fillLength} 100`}
      />
      <circle cx="100" cy="100" r="16" fill="#ffffff" stroke="#0f172a" strokeWidth="4" />
      <line x1="100" y1="100" x2={needleX} y2={needleY} stroke="#0f172a" strokeWidth="4" strokeLinecap="round" />
      <circle cx={needleX} cy={needleY} r="5.5" fill="#0f172a" stroke="#ffffff" strokeWidth="2" />
      <text x="30" y="124" textAnchor="middle" className="report-score-gauge-label">{safeMin}</text>
      <text x="170" y="124" textAnchor="middle" className="report-score-gauge-label">{safeMax}</text>
      <text x="100" y="74" textAnchor="middle" className="report-score-gauge-score">{Math.round(boundedValue)}</text>
    </svg>
  );
}

function InspectionScorePanel({ score, genericResults, bodyResults, resultTypes }) {
  const [animatedValue, setAnimatedValue] = useState(0);

  const scoreValue = Number(score?.score ?? 0) || 0;
  // بلا ?? وحدها كان الحد الأدنى صفر يتحول إلى 300 فينقلب مدى المؤشر.
  const scoreMin = Number(score?.min ?? 0);
  const scoreMax = Number(score?.max ?? 100) || 100;
  const scoreLabel = score?.label || 'تقييم الفحص';
  const scorePercentage = Number(score?.percentage ?? 0) || 0;

  useEffect(() => {
    if (!scoreValue) return;

    const duration = 1400;
    const startTime = performance.now();
    let frame;

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedValue(scoreValue * eased);

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setAnimatedValue(scoreValue);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [scoreValue]);

  const statusKeys = useMemo(
    () => (resultTypes || []).map((type) => String(type.value || '').toLowerCase()),
    [resultTypes],
  );

  const resultCounts = useMemo(() => {
    const counts = Object.fromEntries(statusKeys.map((key) => [key, 0]));

    genericResults.forEach((r) => {
      const key = String(r?.condition_type || '').toLowerCase();
      if (counts[key] !== undefined) counts[key] += 1;
    });

    // الجزء الواحد قد يحمل أكثر من نتيجة (خدش + مرشوش)، فتُحتسب كلها.
    bodyResults.forEach((r) => {
      const selected = readStatusList(r);

      // الأجزاء بلا حالة محددة لا تُحتسب ضمن أي نتيجة.
      if (!selected.length) return;

      selected.forEach((key) => {
        if (counts[key] !== undefined) counts[key] += 1;
      });
    });

    return counts;
  }, [genericResults, bodyResults, statusKeys]);

  const totalResults = Object.values(resultCounts).reduce((sum, count) => sum + count, 0);

  // الألوان والأرقام تأتي من نفس دليل الألوان بدل قائمة مكتوبة يدويًا،
  // حتى يتبع التقرير أي تعديل يجريه المسؤول على الحالات وألوانها.


  return (
    <section className="report-section report-score-panel-section report-safe-block">
      <div className="report-section-head">
        <SectionHeading ar="مؤشر التقييم النهائي" en="Final Rating Indicator" icon="🏁" />
      </div>
      <div className="report-score-panel">
        <div className="report-score-panel-main">
          <div className="report-score-panel-gauge">
            <InspectionScoreGauge value={animatedValue} min={scoreMin} max={scoreMax} />
          </div>
          <div className="report-score-panel-info">
            <div className="report-score-panel-value">{Math.round(animatedValue)}</div>
            <div className="report-score-panel-label">{scoreLabel}</div>
            <div className="report-score-panel-meta">
              <span className="report-score-panel-percentage">{scorePercentage}%</span>
              <span className="report-score-panel-range">{scoreMin} - {scoreMax}</span>
            </div>
          </div>
        </div>

        <div className="report-score-panel-results">
          <div className="report-score-panel-results-title">تفصيل النتائج</div>
          <div className="report-score-panel-results-grid">
            {resultTypes.map((type) => {
              const count = resultCounts[type.value] || 0;
              const pct = totalResults > 0 ? (count / totalResults) * 100 : 0;
              return (
                <div
                  key={type.value}
                  className={`report-score-result-card report-score-result-card--${type.value}`}
                  style={{
                    '--card-color': type.bgColor,
                    '--card-bg': `${type.bgColor}14`,
                    '--card-border': type.borderColor,
                  }}
                >
                  <div className="report-score-result-card-count">{count}</div>
                  <div className="report-score-result-card-label">
                    <span className="report-score-result-card-number">{type.number}</span>
                    {type.label}
                  </div>
                  <div className="report-score-result-card-bar">
                    <div className="report-score-result-card-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {totalResults > 0 && (
            <div className="report-score-panel-distribution">
              {resultTypes.map((type) => {
                const count = resultCounts[type.value] || 0;
                const pct = (count / totalResults) * 100;
                if (pct === 0) return null;
                return (
                  <div
                    key={type.value}
                    className="report-score-distribution-segment"
                    style={{ width: `${pct}%`, background: type.bgColor }}
                    title={`${type.label}: ${count} (${pct.toFixed(1)}%)`}
                  />
                );
              })}
            </div>
          )}
        </div>

      </div>
    </section>
  );
}

function InfoPanel({ ar, en, icon, iconType, className = '', children }) {
  return (
    <section className={`report-info-panel ${className}`}>
      <div className="report-info-panel-head">
        <InfoIcon type={iconType}>{icon}</InfoIcon>
        <div className="report-info-title">
          <span className="report-info-title-en">{en}</span>
          <span className="report-info-title-ar">{ar}</span>
        </div>
        <InfoIcon type={iconType}>{icon}</InfoIcon>
      </div>
      <div className="report-info-panel-body">{children}</div>
    </section>
  );
}

function InfoRow({ value, ar, en, icon, iconType, required = false }) {
  const displayValue = value === 0 || value ? value : '-';

  return (
    <div className="report-info-row">
      <div className="report-info-value">{displayValue}</div>
      <div className="report-info-labels">
        <strong className="report-info-label-ar">{ar}{required ? ' *' : ''}</strong>
        <span className="report-info-label-en">{en}</span>
      </div>
      <InfoIcon type={iconType}>{icon}</InfoIcon>
    </div>
  );
}

function VehiclePlate({ number, code }) {
  const plateNumber = String(number || '').trim();
  const plateCode = String(code || '').trim();

  return (
    <div className="report-vehicle-plate" dir="ltr" aria-label="Vehicle plate">
      <div className="report-vehicle-plate-number">{plateNumber || '-'}</div>
      <div className="report-vehicle-plate-emblem" aria-hidden="true">
        <img src="/2644_R0lVIE5JQyA1ODctMjE.jpg" alt="" />
      </div>
      <div className="report-vehicle-plate-code" dir="rtl">{plateCode || '-'}</div>
      <div className="report-vehicle-plate-side">
        <span>K</span>
        <span>S</span>
        <span>A</span>
      </div>
    </div>
  );
}

function inspectionTypeIsDone(inspection, type) {
  if (!inspection || !type) return false;

  if (type.done_field && Object.prototype.hasOwnProperty.call(inspection, type.done_field)) {
    return Boolean(inspection[type.done_field]);
  }

  if (type.key === 'body') return Boolean((inspection.body_results || []).length);
  if (type.key === 'computer') return Boolean(inspection.computer_result);

  return (inspection.inspection_results || []).some((result) => (
    result?.inspection_item?.section === type.key
    || result?.inspection_item?.inspection_type?.key === type.key
  ));
}

function resultSortTime(result) {
  const updatedAt = result?.updated_at ? new Date(result.updated_at).getTime() : 0;
  const createdAt = result?.created_at ? new Date(result.created_at).getTime() : 0;
  return Number.isFinite(updatedAt) && updatedAt > 0 ? updatedAt : createdAt;
}

function dedupeResultsByItem(results = []) {
  const byItem = new Map();

  results.forEach((result, index) => {
    const itemId = result?.inspection_item_id || result?.inspection_item?.id;
    const key = itemId || `row-${result?.id || index}`;
    const current = byItem.get(key);

    if (!current) {
      byItem.set(key, { result, index });
      return;
    }

    const currentTime = resultSortTime(current.result);
    const nextTime = resultSortTime(result);
    const currentId = Number(current.result?.id || 0);
    const nextId = Number(result?.id || 0);

    if (nextTime > currentTime || (nextTime === currentTime && nextId >= currentId)) {
      byItem.set(key, { result, index: current.index });
    }
  });

  return Array.from(byItem.values())
    .sort((a, b) => a.index - b.index)
    .map((entry) => entry.result);
}

export default function Report() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [creatingShareLink, setCreatingShareLink] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [showWhatsAppShare, setShowWhatsAppShare] = useState(false);
  const [whatsAppCountryCode, setWhatsAppCountryCode] = useState('966');
  const [whatsAppPhone, setWhatsAppPhone] = useState('');
  const [genericResultOptions, setGenericResultOptions] = useState(DEFAULT_GENERIC_RESULT_OPTIONS);
  const [bodyResultOptions, setBodyResultOptions] = useState(DEFAULT_BODY_RESULT_OPTIONS);
  const [companySettings, setCompanySettings] = useState(null);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const genericResultMap = buildResultOptionMap(genericResultOptions);
  const genericResultMapByKey = useMemo(
    () => Object.fromEntries(genericResultOptions.map((option) => [String(option.value || '').toLowerCase(), option])),
    [genericResultOptions],
  );
  const bodyResultMap = buildResultOptionMap(bodyResultOptions);
  // نفس ترتيب وأرقام الدليل المستخدم في الرسم، حتى يتطابق الرقمان.
  const numberedBodyResultOptions = useMemo(
    () => getNumberedResultOptions(bodyResultOptions),
    [bodyResultOptions],
  );
  const bodyResultMapByKey = useMemo(
    () => Object.fromEntries(bodyResultOptions.map((option) => [String(option.value || '').toLowerCase(), option])),
    [bodyResultOptions],
  );
  const reportDate = formatReportDate(data?.inspection_date || data?.formatted_created_at || data?.created_at);
  const searchParams = new URLSearchParams(window.location.search);
  const isPdfMode = searchParams.get('pdf') === '1';
  const themeFromUrl = (searchParams.get('theme') || '').toLowerCase();
  const [theme, setTheme] = useState(() => {
    if (REPORT_THEMES.some((item) => item.key === themeFromUrl)) return themeFromUrl;
    const saved = String(localStorage.getItem('reportTheme') || '').toLowerCase();
    return REPORT_THEMES.some((item) => item.key === saved) ? saved : 'executive';
  });
  const headerCompanyName = companySettings?.company_name || COMPANY_NAME;
  const reportBranch = data?.auction || data?.branch || {};
  const headerContactInfo = [
    reportBranch.name,
    reportBranch.phone || companySettings?.company_phone,
    reportBranch.address,
    companySettings?.company_email,
  ].filter(Boolean);
  const headerCompanyInfo = headerContactInfo.join(' • ') || COMPANY_INFO_LINE;
  const headerCompanyLogo = logoUrl(companySettings?.company_logo_path, COMPANY_LOGO);
  const vehicle = data?.vehicle || {};
  const headerAuctionImage = storageUrl(reportBranch.image_path);
  const clientName = String(vehicle.owner?.name || '').trim();
  const shouldShowClientName = clientName && clientName !== '-' && clientName !== 'غير محدد';
  const manufacturerLogo = storageUrl(vehicle.manufacturer?.logo_path) || headerCompanyLogo;
  const inspectionScore = data?.inspection_score || {};
  const categoryName = vehicle.category?.name;
  const modelName = vehicle.vehicle_model?.name || vehicle.vehicleModel?.name;
  const vehicleName = [
    categoryName,
    modelName,
  ].filter(Boolean).join(' ');
  const reportNumber = data?.display_number || data?.branch_serial || id;
  const selectedInspectionTypes = inspectionTypes
    .filter((type) => type.is_active && inspectionTypeIsDone(data, type))
    .map((type) => type.name)
    .filter(Boolean);
  const inspectionLabel = selectedInspectionTypes.length
    ? selectedInspectionTypes.join('، ')
    : data?.inspection_type?.name || data?.inspectionType?.name || data?.type?.name || '-';
  const findInspectionType = (key) => inspectionTypes.find((type) => type.key === key);
  const computerInspectionType = findInspectionType('computer');
  const bodyInspectionType = findInspectionType('body');
  const genericResultRows = dedupeResultsByItem(data?.inspection_results || []);
  const bodyResultRows = dedupeResultsByItem(data?.body_results || []);
  const genericResultGroups = Array.from(genericResultRows.reduce((groups, result) => {
    const itemType = result?.inspection_item?.inspection_type;
    const sectionKey = itemType?.key || result?.inspection_item?.section || 'inspection';
    const type = findInspectionType(sectionKey) || itemType || {};

    if (!groups.has(sectionKey)) {
      groups.set(sectionKey, {
        key: sectionKey,
        ar: type.name || itemType?.name || 'نتائج الفحص',
        en: type.name_en || itemType?.name_en || 'Inspection Results',
        icon: type.icon || '',
        rows: [],
      });
    }

    groups.get(sectionKey).rows.push(result);
    return groups;
  }, new Map()).values());
  const bodyItemIds = new Set(bodyResultRows.map((row) => row?.inspection_item_id).filter(Boolean));
  const bodyAttachmentMap = (data?.attachments || []).reduce((acc, attachment) => {
    const section = String(attachment?.section || '').toLowerCase();
    const itemId = attachment?.inspection_item_id || attachment?.inspection_item?.id;
    const isBodyAttachment = section === 'body' || section.includes('body') || bodyItemIds.has(itemId);

    if (!isBodyAttachment || !itemId) return acc;
    if (!acc[itemId]) acc[itemId] = [];
    acc[itemId].push(attachment);
    return acc;
  }, {});
  const attachmentMapByItemId = (data?.attachments || []).reduce((acc, attachment) => {
    const itemId = attachment?.inspection_item_id || attachment?.inspection_item?.id;
    if (!itemId) return acc;
    if (!acc[itemId]) acc[itemId] = [];
    acc[itemId].push(attachment);
    return acc;
  }, {});
  const resultByItemId = new Map();
  genericResultRows.forEach((result) => {
    if (result?.inspection_item_id) resultByItemId.set(result.inspection_item_id, result);
  });
  bodyResultRows.forEach((result) => {
    if (result?.inspection_item_id) resultByItemId.set(result.inspection_item_id, result);
  });
  const attachmentCaption = (attachment) => {
    const itemId = attachment?.inspection_item_id || attachment?.inspection_item?.id;
    const result = itemId ? resultByItemId.get(itemId) : null;
    const itemName = String(attachment?.inspection_item?.name || result?.inspection_item?.name || attachment?.section || '').trim();
    const status = result?.condition_status
      ? bodyStatusLabel(result)
      : (
          genericResultMap[result?.condition_type]?.label
          || genericResultMapByKey[String(result?.condition_type || '').toLowerCase()]?.label
          || result?.condition_type
          || ''
        );

    return [itemName, status].filter(Boolean).join(' - ') || '-';
  };
  const attachmentImagePath = (attachment, { preferThumbnail = false } = {}) => {
    if (!attachment) return '';
    if (isPdfMode) {
      if (attachment.thumbnail_path) return attachment.thumbnail_path;
      if (preferThumbnail && attachment.optimized_image_path) return attachment.optimized_image_path;
      return attachment.optimized_image_path || '';
    }
    return attachment.optimized_image_path || attachment.file_path || '';
  };
  const firstAttachmentForItem = (itemId) => {
    const attachments = attachmentMapByItemId[itemId] || [];
    return attachments.find((attachment) => attachmentImagePath(attachment));
  };

  // الصور تُقسَّم إلى صفحات ثابتة (٩ صور لكل صفحة) بدل شبكة واحدة طويلة،
  // فلا تنقطع صورة بين صفحتين في PDF ويبقى ترتيب العرض مفهومًا للعميل.
  const galleryPages = (() => {
    const usable = (data?.attachments || []).filter((attachment) => attachmentImagePath(attachment));
    const pages = [];
    for (let index = 0; index < usable.length; index += GALLERY_IMAGES_PER_PAGE) {
      pages.push(usable.slice(index, index + GALLERY_IMAGES_PER_PAGE));
    }
    return pages;
  })();

  // كل الحالات المختارة للجزء (قد تكون أكثر من واحدة: خدش + مرشوش).
  const bodyStatusKeys = (result) => {
    const list = readStatusList(result);
    return list.length ? list : [bodyStatusKey(result)];
  };

  const bodyStatusKey = (result) => {
    const raw = String(result?.condition_status || '').toLowerCase();
    if (bodyResultMapByKey[raw]) return bodyResultMapByKey[raw].value;
    if (['good', 'scratch', 'damage', 'sprayed', 'riveted', 'replaced', 'defect'].includes(raw)) return raw;
    if (result?.is_riveted) return 'riveted';
    if (result?.is_replaced || result?.is_dented) return 'defect';
    if (result?.is_modified || result?.is_sprayed || result?.is_loose || result?.is_plastic || result?.is_protected) {
      return 'damage';
    }
    // بلا أي حالة محفوظة = «غير محدد». إظهارها «جيد» كان يمنح الجزء نتيجة لم تُختر.
    return 'unknown';
  };

  const bodyStatusLabel = (result) => {
    const key = bodyStatusKey(result);
    const option = bodyResultMap[key] || bodyResultMapByKey[String(key || '').toLowerCase()];
    return option?.label || {
      good: 'جيد',
      scratch: 'خدش',
      damage: 'ضرر',
      sprayed: 'مرشوش',
      riveted: 'مسمكر',
      replaced: 'مبدل',
      defect: 'تالف',
    }[key] || 'غير محدد';
  };

  const labelForStatusKey = (key) => {
    const option = bodyResultMap[key] || bodyResultMapByKey[String(key || '').toLowerCase()];
    return option?.label || bodyStatusLabel({ condition_status: key });
  };

  const styleForStatusKey = (key) => {
    const option = bodyResultMap[key] || bodyResultMapByKey[String(key || '').toLowerCase()];
    if (!option || ['good', 'scratch', 'damage', 'sprayed', 'riveted', 'replaced', 'defect'].includes(key)) return undefined;
    return {
      background: option.bgColor,
      borderColor: option.borderColor,
      color: option.textColor,
    };
  };

  const genericStatusClass = (value) => {
    const key = String(value || '').toLowerCase();
    return ['good', 'scratch', 'damage', 'sprayed', 'riveted', 'replaced', 'defect'].includes(key)
      ? `report-pill--${key}`
      : '';
  };
  const renderItemName = (item) => {
    const arName = item?.name || '-';
    const enName = String(item?.name_en || '').trim();

    if (!enName) return arName;

    return (
      <div className="report-item-name-cell">
        <div className="ar">{arName}</div>
        <div className="en">{enName}</div>
      </div>
    );
  };
  const renderItemWithAttachment = (item, itemId) => {
    const attachment = firstAttachmentForItem(itemId || item?.id);
    const imagePath = attachmentImagePath(attachment, { preferThumbnail: true });

    return (
      <div className="report-item-with-image">
        {imagePath && (
          <img
            src={storageUrl(imagePath)}
            alt={item?.name || 'inspection item'}
            className="report-item-thumb"
          />
        )}
        <div className="report-item-title">{renderItemName(item)}</div>
      </div>
    );
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadInspectionReportPdf(id, { theme });
    } catch (err) {
      setError(err?.message || 'تعذر تنزيل التقرير PDF الآن. حاول مرة أخرى.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const generateShareLink = async () => {
    setError('');
    setCreatingShareLink(true);
    try {
      const { url } = await createInspectionReportShareLink(id, { theme });
      setShareLink(url);
    } catch (err) {
      setError(err?.message || 'تعذر إنشاء رابط مشاركة التقرير الآن. حاول مرة أخرى.');
    } finally {
      setCreatingShareLink(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;

    try {
      await navigator.clipboard.writeText(shareLink);
      window.alert('تم نسخ رابط التقرير بنجاح.');
    } catch {
      window.prompt('انسخ رابط التقرير يدويًا:', shareLink);
    }
  };

  const shareMessage = [
    `مرحبًا، ${headerCompanyName} يرفق لكم رابط تقرير فحص المركبة.`,
    `المركبة: ${vehicleName || 'غير محددة'}`,
    `رقم الفحص: ${reportNumber}`,
    '',
    shareLink,
  ].join('\n');
  const normalizedWhatsAppPhone = whatsAppPhone.replace(/\D/g, '').replace(/^0+/, '');
  const canSendWhatsAppToNumber = normalizedWhatsAppPhone.length >= 5;
  const openWhatsAppShare = (phone = '') => {
    if (!shareLink) return;

    const text = encodeURIComponent(shareMessage);
    const url = phone
      ? `https://wa.me/${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const sendWhatsAppToNumber = () => {
    if (!canSendWhatsAppToNumber) return;
    openWhatsAppShare(`${whatsAppCountryCode}${normalizedWhatsAppPhone}`);
  };

  useEffect(() => {
    if (!isPdfMode) {
      localStorage.setItem('reportTheme', theme);
    }
  }, [theme, isPdfMode]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [reportRes, genericRes, bodyRes, settingsRes, typesRes] = await Promise.all([
          api.get(`/inspections/${id}/report`),
          api.get(`/lookups/by-type/${getResultLookupType('generic')}`).catch(() => ({ data: [] })),
          api.get(`/lookups/by-type/${getResultLookupType('body')}`).catch(() => ({ data: [] })),
          api.get('/settings/company').catch(() => ({ data: null })),
          api.get('/inspection-types').catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        setData(reportRes.data);
        setGenericResultOptions(normalizeResultOptions(genericRes.data, 'generic'));
        setBodyResultOptions(normalizeResultOptions(bodyRes.data, 'body'));
        setCompanySettings(settingsRes.data || null);
        setInspectionTypes(typesRes.data || []);
      } catch (err) {
        if (!mounted) return;
        if (err?.code === 'ECONNABORTED' || String(err?.message || '').includes('timeout')) {
          setError('تعذر تحميل التقرير لأن الخادم استغرق وقتًا طويلًا. حاول مرة أخرى.');
        } else {
          setError(err?.response?.data?.message || 'حدث خطأ أثناء تحميل التقرير.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [id]);

  return (
    <AppShell
      title={`التقرير النهائي #${reportNumber}`}
      showMobileMenuActions={false}
    >
      {!isPdfMode && (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="inline-flex w-full flex-wrap gap-2 rounded-2xl border border-slate-300 bg-white p-3 shadow-sm">
              {REPORT_THEMES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`report-theme-btn ${theme === item.key ? 'active' : ''}`}
                  onClick={() => setTheme(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[460px]">
              <button className="report-action-btn w-full" onClick={downloadPdf} disabled={downloadingPdf}>
              {downloadingPdf ? 'جاري تجهيز PDF...' : 'تحميل PDF'}
              </button>
              <button className="report-action-btn w-full" onClick={generateShareLink} disabled={creatingShareLink}>
              {creatingShareLink ? 'جاري إنشاء الرابط...' : 'مشاركة رابط PDF'}
              </button>
              <button className="report-action-btn w-full" onClick={() => window.print()}>
              طباعة
              </button>
            </div>
          </div>
        </div>
      )}
      {loading ? (
        <p>جاري تحميل التقرير...</p>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 text-sm">{error}</div>
      ) : (
        <div
          className={`report-root report-print-wrapper theme-${theme} ${isPdfMode ? 'report-pdf-mode' : ''}`}
          style={{
            backgroundImage: `radial-gradient(circle at top right, rgba(184, 15, 26, 0.12), transparent 32%), radial-gradient(circle at bottom left, rgba(9, 20, 46, 0.1), transparent 28%)`,
            backgroundRepeat: 'no-repeat, no-repeat',
            backgroundSize: 'auto, auto',
            backgroundPosition: 'top right, bottom left',
          }}
        >
          {!isPdfMode && shareLink && (
            <section className="report-section report-safe-block">
              <div className="report-section-head">
                <SectionHeading ar="رابط مشاركة التقرير" en="Report Share Link" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareLink}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                />
                <button type="button" className="report-action-btn" onClick={copyShareLink}>نسخ الرابط</button>
                <a
                  className="report-action-btn"
                  href={shareLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  فتح الرابط
                </a>
                <button type="button" className="report-action-btn" onClick={() => setShowWhatsAppShare(true)}>
                  مشاركة واتساب
                </button>
              </div>
            </section>
          )}

          {!isPdfMode && showWhatsAppShare && (
            <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/55 p-4 pt-20 sm:pt-24">
              <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-2xl" dir="rtl">
                <div className="bg-emerald-600 px-5 py-4 text-white">
                  <div className="text-xl font-bold">مشاركة التقرير عبر واتساب</div>
                  <div className="mt-1 text-sm text-emerald-50">أدخل رقم المستلم أو شارك الرابط واختر المستلم من واتساب.</div>
                </div>

                <div className="space-y-4 p-5">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">مفتاح الدولة</label>
                    <select
                      value={whatsAppCountryCode}
                      onChange={(e) => setWhatsAppCountryCode(e.target.value)}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none"
                    >
                      {ARAB_COUNTRY_CODES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.dial} - {country.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">رقم الواتساب</label>
                    <div className="flex overflow-hidden rounded-xl border border-slate-300 bg-white focus-within:border-emerald-500">
                      <span className="flex items-center border-l border-slate-200 px-3 text-sm font-bold text-slate-600" dir="ltr">
                        +{whatsAppCountryCode}
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={whatsAppPhone}
                        onChange={(e) => setWhatsAppPhone(e.target.value)}
                        placeholder="5XXXXXXXX"
                        className="min-w-0 flex-1 px-3 py-3 text-left text-sm outline-none"
                        dir="ltr"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={sendWhatsAppToNumber}
                      disabled={!canSendWhatsAppToNumber}
                      className="mt-3 w-full rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      إرسال
                    </button>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-6 text-slate-600">
                    سيتم فتح واتساب مع رسالة تحتوي على رابط التقرير، ثم يمكنك إرسالها مباشرة.
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                    <button
                      type="button"
                      onClick={() => openWhatsAppShare()}
                      className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-100"
                    >
                      مشاركة فقط
                    </button>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => setShowWhatsAppShare(false)}
                        className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
                      >
                        إغلاق
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="report-screen-header">
            <div className="report-screen-header-info">
              <strong>{headerCompanyName}</strong>
              <span>{headerCompanyInfo}</span>
            </div>
            <div className="report-screen-header-title">
              <h2>تقرير فحص مركبة #{reportNumber}</h2>
              <p>{reportDate}</p>
            </div>
            <div className="report-screen-header-logos">
              {headerAuctionImage && (
                <img src={headerAuctionImage} alt={reportBranch.name || 'auction'} />
              )}
              <img src={headerCompanyLogo} alt="logo" />
            </div>
          </div>

          {!isPdfMode && (
            <>
              <div className="report-print-header">
                <div className="report-print-header-side start">
                  <div className="report-company-name">{headerCompanyName}</div>
                  <div>{headerCompanyInfo}</div>
                </div>
                <div className="report-print-header-center">
                  <div className="report-print-header-logos">
                    {headerAuctionImage && (
                      <img src={headerAuctionImage} alt={reportBranch.name || 'auction'} />
                    )}
                    <img src={headerCompanyLogo} alt="logo" />
                  </div>
                  <div>
                    <div className="report-print-header-title">تقرير فحص مركبة #{reportNumber}</div>
                    <div className="report-print-header-sub">{headerCompanyName}</div>
                  </div>
                </div>
                <div className="report-print-header-side end">
                  <div>التاريخ</div>
                  <div>{reportDate}</div>
                </div>
              </div>
              <div className="report-header-spacer" aria-hidden="true" />
            </>
          )}

          <div className="report-cover-page">
          <section className="report-hero report-safe-block">
            <div className="report-hero-heading">
              <div className="report-hero-heading-ar">تقرير فحص مركبة</div>
              <div className="report-hero-heading-en">Vehicle inspection report</div>
            </div>
            <div className="report-vehicle-summary">
              <VehiclePlate number={vehicle.plate_number} code={vehicle.plate_code} />
              <div className="report-vehicle-brand">
                <img src={manufacturerLogo} alt={vehicle.manufacturer?.name || 'vehicle brand'} />
              </div>
              <div className="report-vehicle-identity">
                <strong>{vehicleName || '-'}</strong>
                {!vehicleName && <span>{`رقم التقرير #${reportNumber}`}</span>}
              </div>
            </div>
          </section>

          <div className="report-info-layout report-safe-block">
            <InfoPanel ar="معلومات الطلب" en="Order Information" iconType="clipboard">
              <InfoRow value={reportNumber} ar="رقم الطلب" en="Order Number" iconType="document" />
              <InfoRow value={reportBranch.name} ar="المزاد" en="Auction" iconType="document" />
              <InfoRow value={inspectionLabel} ar="نوع الفحص" en="Inspection Type" iconType="refresh" />
              {shouldShowClientName && (
                <InfoRow value={clientName} ar="العميل" en="Client" iconType="vehicle" />
              )}
            </InfoPanel>

            <InfoPanel ar="معلومات المركبة" en="Vehicle Information" iconType="vehicle">
              <InfoRow value={vehicle.vin} ar="رقم الهيكل" en="Vehicle VIN" iconType="barcode" />
              <InfoRow value={data.vehicle_id || vehicle.id || 0} ar="الرقم التسلسلي" en="Serial Number" iconType="serial" />
              <InfoRow value={vehicle.odometer} ar="قراءة العداد" en="Meter Reading" iconType="odometer" />
            </InfoPanel>

            <InfoPanel ar="معلومات الهيكل" en="Structure Information" iconType="structure" className="report-info-panel--tall">
              <InfoRow value={vehicle.drive_type} ar="نوع الدفع" en="Propulsion Type" iconType="structure" />
              <InfoRow value={vehicle.body_shape || data.diagram_body_style} ar="شكل الهيكل" en="Structure shape" iconType="topCar" />
              <InfoRow value={vehicle.seat_type} ar="نوع المقاعد" en="Type of Seats" iconType="seat" />
              <InfoRow value={vehicle.seats_count} ar="عدد المقاعد" en="Number of Seats" iconType="seats" />
              <InfoRow value={vehicle.exterior_color} ar="اللون الخارجي" en="Exterior Color" iconType="paint" />
              <InfoRow value={vehicle.interior_color} ar="اللون الداخلي" en="Interior Color" iconType="paint" />
            </InfoPanel>

            <div className="report-info-side-stack">
              <InfoPanel ar="معلومات المحرك والقير" en="Engine and Gear" iconType="flame">
                <InfoRow value={vehicle.cylinders} ar="السلندرات" en="Cylinders" iconType="cylinder" />
                <InfoRow value={vehicle.engine_size} ar="سعة المحرك" en="Engine Size" iconType="engine" />
                <InfoRow value={vehicle.fuel_type} ar="نوع الوقود" en="Fuel Type" iconType="fuel" />
                <InfoRow value={vehicle.gear_type || vehicle.transmission} ar="نوع القير" en="Gear Type" iconType="gear" />
              </InfoPanel>
              {inspectionScore.score != null && (
                <div className="report-cover-gauge">
                  <InspectionScoreGauge
                    value={inspectionScore.score}
                    min={inspectionScore.min}
                    max={inspectionScore.max}
                    gradientId="reportCoverScoreGradient"
                  />
                </div>
              )}
            </div>
          </div>
          </div>

          {genericResultGroups.map((group) => (
            <section key={group.key} className="report-section report-inspection-section report-safe-block">
              <div className="report-section-head">
                <SectionHeading ar={group.ar} en={group.en} icon={group.icon} />
              </div>
              <div className="report-table-wrap">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>البند</th>
                      <th>الحالة</th>
                      <th>الملاحظات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((r) => (
                      <tr key={r.id} className="report-safe-row">
                        <td>{renderItemWithAttachment(r.inspection_item, r.inspection_item_id)}</td>
                        <td>
                          <span className={`report-pill ${genericStatusClass(r.condition_type)}`}>
                            {genericResultMap[r.condition_type]?.label || genericResultMapByKey[String(r.condition_type || '').toLowerCase()]?.label || r.condition_type || '-'}
                          </span>
                        </td>
                        <td className="report-multiline">{r.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

        <section className="report-section report-inspection-section report-safe-block">
            <div className="report-section-head">
              <SectionHeading
                ar={computerInspectionType?.name || 'نتيجة فحص الكمبيوتر'}
                en={computerInspectionType?.name_en || 'Computer Scan Result'}
                icon={computerInspectionType?.icon}
              />
            </div>
            <div className="report-computer-grid">
              <div className="report-computer-card">
                <span>النتيجة</span>
                <strong className="report-multiline">{data.computer_result?.scan_result || '-'}</strong>
              </div>
              <div className="report-computer-card">
                <span>الفحص الميداني</span>
                <strong className="report-multiline">{data.computer_result?.field_inspection || '-'}</strong>
              </div>
              <div className="report-computer-card">
                <span>الملاحظات</span>
                <strong className="report-multiline">{data.computer_result?.notes || '-'}</strong>
              </div>
            </div>
          </section>

          <section className="report-section report-inspection-section report-body-section">
            <div className="report-section-head">
              <SectionHeading
                ar={bodyInspectionType?.name || 'نتائج فحص البودي'}
                en={bodyInspectionType?.name_en || 'Body Inspection'}
                icon={bodyInspectionType?.icon}
              />
            </div>

            <div className="report-body-color-guide report-safe-block" aria-label="دليل ألوان وأرقام نتائج البودي">
              <div className="report-body-color-guide-title">
                دليل الألوان والأرقام
                <span className="report-body-color-guide-hint">الرقم على كل جزء في الرسم يدل على حالته</span>
              </div>
              <div className="report-body-color-guide-items">
                {numberedBodyResultOptions.map((option) => {
                  const isDefault = ['good', 'scratch', 'damage', 'defect', 'riveted'].includes(option.value);
                  return (
                    <span
                      key={option.value}
                      className={`report-pill report-pill--body ${isDefault ? `report-pill--${option.value}` : ''}`}
                      style={isDefault ? undefined : { background: option.bgColor, borderColor: option.borderColor, color: option.textColor }}
                    >
                      <span className="report-pill-number">{option.number}</span>
                      {option.label}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="report-body-diagram report-safe-block">
              <BodyDamageDiagram
                bodyStyle={data.diagram_body_style || 'sedan'}
                items={bodyResultRows.map((r) => r.inspection_item).filter(Boolean)}
                values={bodyResultRows.reduce((acc, r) => {
                  acc[r.inspection_item_id] = r;
                  return acc;
                }, {})}
                title="رسم السيارة مع أماكن الضرر"
                resultOptions={bodyResultOptions}
                showLegend={false}
                showZoneSummary={false}
                preferRasterAssets={isPdfMode}
              />
            </div>

            <div className="report-table-wrap report-safe-block">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الجزء</th>
                    <th>الحالة</th>
                    <th>ملاحظات</th>
                    <th>المرفق</th>
                  </tr>
                </thead>
                <tbody>
                  {bodyResultRows.map((r, index) => {
                    const itemId = r.inspection_item_id;
                    const relatedAttachments = bodyAttachmentMap[itemId] || [];
                    const firstAttachment = relatedAttachments[0];
                    const firstAttachmentPath = attachmentImagePath(firstAttachment, { preferThumbnail: true });

                    return (
                      <tr key={r.id} className="report-safe-row">
                        <td>{index + 1}</td>
                        <td>{renderItemWithAttachment(r.inspection_item, r.inspection_item_id)}</td>
                        <td>
                          <span className="report-body-status-list">
                            {bodyStatusKeys(r).map((statusKey) => (
                              <span
                                key={statusKey}
                                className={`report-pill report-pill--body report-pill--${statusKey}`}
                                style={styleForStatusKey(statusKey)}
                              >
                                {labelForStatusKey(statusKey)}
                              </span>
                            ))}
                          </span>
                        </td>
                        <td className="report-multiline">{r.notes || '-'}</td>
                        <td className="report-body-attachment-cell">
                          {firstAttachmentPath ? (
                            <div className="report-body-attachment-wrap">
                              <img
                                src={storageUrl(firstAttachmentPath)}
                                alt={r.inspection_item?.name || 'body-attachment'}
                                className="report-body-thumb"
                              />
                              {relatedAttachments.length > 1 && (
                                <span className="report-body-attachment-count">+{relatedAttachments.length - 1}</span>
                              )}
                            </div>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="report-section report-safe-block">
            <div className="report-section-head">
              <SectionHeading ar="صور المركبة" en="Vehicle Pictures" />
            </div>
            {galleryPages.map((pageItems, pageIndex) => (
              <div className="report-gallery-page" key={`gallery-page-${pageIndex}`}>
                <div className="report-gallery-grid">
                  {pageItems.map((a) => {
                    const caption = attachmentCaption(a);
                    const imagePath = attachmentImagePath(a);

                    return (
                      <figure key={a.id} className="report-gallery-card report-safe-row">
                        <img
                          src={storageUrl(imagePath)}
                          alt={caption || 'attachment'}
                          className="report-safe-image"
                        />
                        <figcaption>
                          <span className="report-gallery-note">{caption}</span>
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>

          <InspectionScorePanel
            score={inspectionScore}
            genericResults={genericResultRows}
            bodyResults={bodyResultRows}
            resultTypes={numberedBodyResultOptions}
          />

          <footer className="report-screen-footer report-credit-footer">
            <div className="report-credit-text">
              تم طباعة التقرير بواسطة نظام{' '}
              <a href="mailto:MAXITS2024@GMAIL.COM">
                MaxitsScanCar فحص السيارات لشركة MaxItS
              </a>
            </div>
          </footer>
        </div>
      )}
    </AppShell>
  );
}
